import React, { useCallback } from 'react';
import {
  ErrorResult,
  ExceptionResult,
  isSuccessful,
  useCCPCreatePaymentMethodAndConfirmCardSetup,
} from '@atlassian/commerce-ui/credit-card-ccp';
import type { CreditCardError } from '@atlassian/commerce-credit-card-base';
import { createPaypalPaymentMethod } from '@atlassian/temp-proxies';
import { usePaymentUpdateNotification } from './use-payment-update-notification';
import { OnUpdateCallbacks, StripeErrorTranslator } from './types';

export enum Currency {
  JPY = 'JPY',
  USD = 'USD',
}

const getCurrencyByCountry = (countryCode?: string | null): string => {
  if (countryCode === 'JP') {
    return Currency.JPY;
  }
  return Currency.USD;
};

export const useAddPaymentMethod = ({
  onUpdateDone,
  onUpdateFailure,
  onUpdateSuccess,
  successMessage,
  failureMessage,
  translateErrorMessage,
  overrideOnUpdateSuccess,
  overrideOnUpdateFailure,
}: OnUpdateCallbacks) => {
  const { notifySuccess, notifyFailure } = usePaymentUpdateNotification('add');
  const [isAddOperationInProgress, setIsAddOperationInProgress] = React.useState(false);
  const [isAddPaypalOperationInProgress, setIsAddPaypalOperationInProgress] = React.useState(false);

  const createPaymentMethodAndConfirmCardSetup = useCCPCreatePaymentMethodAndConfirmCardSetup();

  const getOrTranslateError = (err: ErrorResult<CreditCardError> | ExceptionResult<unknown>) => {
    if (translateErrorMessage) {
      const errorMessage = translateErrorMessage(err);
      return errorMessage ?? failureMessage;
    }
    return failureMessage;
  };

  const onSuccess = (result?: any) => {
    if (overrideOnUpdateSuccess) {
      overrideOnUpdateSuccess(result);
      setIsAddOperationInProgress(false);
      setIsAddPaypalOperationInProgress(false);
    } else {
      notifySuccess(successMessage);
      setIsAddPaypalOperationInProgress(false);
      if (onUpdateSuccess) {
        onUpdateSuccess();
      }
    }
  };

  const onFailure = (errorMessage: ReturnType<typeof getOrTranslateError>) => {
    if (overrideOnUpdateFailure) {
      overrideOnUpdateFailure(errorMessage as string);
      setIsAddOperationInProgress(false);
      setIsAddPaypalOperationInProgress(false);
    } else {
      notifyFailure(errorMessage as string);
      setIsAddOperationInProgress(false);
      onUpdateFailure && onUpdateFailure();
      setIsAddPaypalOperationInProgress(false);
    }
  };

  const onSubmit = useCallback(async () => {
    setIsAddOperationInProgress(true);
    try {
      if (!createPaymentMethodAndConfirmCardSetup) {
        return;
      }
      const result = await createPaymentMethodAndConfirmCardSetup();
      if (!isSuccessful(result)) {
        const errorMessage = getOrTranslateError(result);
        onFailure(errorMessage);
        return;
      }
      onSuccess();
    } catch (err) {
      notifyFailure(failureMessage as string);
      setIsAddOperationInProgress(false);
      if (onUpdateFailure) {
        onUpdateFailure();
      }
    } finally {
      onUpdateDone && onUpdateDone();
    }
  }, [createPaymentMethodAndConfirmCardSetup]);

  const onPaypalSubmit = useCallback(async (transactionAccountId, createPaymentMethodContext) => {
    setIsAddPaypalOperationInProgress(true);
    const paypalDetails = createPaymentMethodContext?.paypal?.paypalDetails;
    const countryCode = paypalDetails?.details?.countryCode;

    const requestPayload = {
      currency: getCurrencyByCountry(countryCode),
      type: createPaymentMethodContext?.paymentMethodType,
      payPal: {
        name: paypalDetails?.details?.firstName + ' ' + paypalDetails?.details?.lastName,
        email: paypalDetails?.details?.email,
        nonce: paypalDetails?.nonce,
      },
    };

    try {
      const result = await createPaypalPaymentMethod({
        transactionAccountId,
        requestPayload,
      });
      if (result?.errorDetails?.reason) {
        onFailure(result.errorDetails.reason);
        return;
      }
      onSuccess(result);
    } catch (err) {
      notifyFailure(failureMessage as string);
      setIsAddOperationInProgress(false);
      if (onUpdateFailure) {
        onUpdateFailure();
      }
    } finally {
      onUpdateDone && onUpdateDone();
    }
  }, []);

  return {
    isOperationInProgress: isAddOperationInProgress,
    onAddPaymentMethod: onSubmit,
    isPaypalOperationInProgress: isAddPaypalOperationInProgress,
    onAddPaypalPaymentMethod: onPaypalSubmit,
  };
};
